import { useRef, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api";
import * as Profile from "@/components/auth/Profile";
import { FormButton } from "@/components/form/FormButton";
import { FormFieldError } from "@/components/form/FormField";
import { twMerge } from "@/utils/twMerge";

import type { TeamModel } from "@/models/TeamModel";

type Props = {
  team: TeamModel;
  size?: "default" | "large";
  className?: string;
};

export const UploadTeamAvatarForm = observer(function UploadTeamAvatarForm({
  team,
  size = "default",
  className,
}: Props) {
  const t = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const triggerFileInput = () => {
    inputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (isSubmitting || !file) {
      return;
    }

    setIsSubmitting(true);
    setServerError(null);

    try {
      await team.uploadAvatar({ avatar: { file } });
    } catch (error) {
      setServerError(getApiErrorMessage(error));
    }

    setIsSubmitting(false);
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        id="upload-avatar"
        type="file"
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
        aria-label={t("teams.updateForm.uploadAvatar")}
      />
      <label
        htmlFor="upload-avatar"
        className={twMerge(
          "flex flex-row items-center cursor-pointer sm:justify-center",

          size === "large" && "rounded-full overflow-hidden",
          size === "large" && "w-[232px] h-[232px]",
          size === "large" && "mx-auto my-8",
        )}
      >
        {size === "default" ? (
          <>
            <Profile.Userpic
              src={team.avatarUrls?.[120]}
              name={team.title || ""}
              setColorFrom={team.uid}
              className={twMerge(
                "text-[30px] w-[60px] h-[60px] mr-5",
                "sm:w-16 sm:h-16 sm:text-16",
              )}
            />
            <FormButton
              isBusy={isSubmitting}
              variant="accent"
              size="sm"
              type="button"
              className="w-full max-w-[150px]"
              onClick={triggerFileInput}
            >
              {t("teams.updateForm.uploadAvatar")}
            </FormButton>
          </>
        ) : (
          <button
            onClick={triggerFileInput}
            type="button"
            className={twMerge(
              "flex items-center justify-center rounded-full overflow-hidden",
              "w-[232px] h-[232px]",
              "border border-gray-200 dark:border-gray-800",
              "hover:opacity-60",
              "active:opacity-40",
            )}
          >
            {team.avatarUrls?.[120] ? (
              <Profile.Userpic
                src={team.avatarUrls?.[120]}
                name={team.title || ""}
                setColorFrom={team.uid}
                className={twMerge("w-full h-full")}
              />
            ) : (
              <span className="text-blue-500 text-[18px] leading-[24px] tracking-tighter font-geist">
                {t("createTeamDialog.stepAvatar.placeholder")}
              </span>
            )}
          </button>
        )}
      </label>
      <FormFieldError message={serverError} />
    </div>
  );
});
